/**
 * Helpers for the per-day plan (the "Today" list).
 *
 * The list is a thin value object — an ordered set of node ids plus the local
 * date it was planned for — so everything here is pure and side-effect free.
 * Ordering is meaningful (top = do first) and is never sorted implicitly.
 */

import { TodoTree, TodayList, ROOT_NODE_NAME } from '../types/todo';

/** A plan that has never been made. Shared constant to keep references stable. */
export const EMPTY_TODAY_LIST: TodayList = { date: '', items: [] };

/** Matches the local calendar dates written to disk. */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format a date as a local YYYY-MM-DD string.
 *
 * Deliberately not `toISOString()`: that converts to UTC, so an evening plan
 * would be stamped with tomorrow's date for users east of Greenwich and the
 * plan would look stale the moment it was made.
 */
export function getLocalDateString(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Whether a plan was made for the given day (defaults to now). */
export function isTodayListCurrent(today: TodayList, now: Date = new Date()): boolean {
  return today.date !== '' && today.date === getLocalDateString(now);
}

/**
 * Whether a task can be added to a day plan: it must exist, be actionable
 * (not done, blocked, or archived), and not be the root container.
 */
export function isEligibleForToday(tree: TodoTree, id: string): boolean {
  const item = tree[id];
  if (!item) return false;
  if (item.name === ROOT_NODE_NAME) return false;
  return !item.archived && !item.done && !item.blocked;
}

/**
 * Drop ids that can no longer be worked on, plus duplicates.
 *
 * Completed items are kept — finishing a task should not make it vanish from
 * the day's list. Archived and deleted ones are removed, which is how the list
 * self-heals after edits made elsewhere in the tree.
 *
 * Returns the original object when nothing changed so callers can use identity
 * to skip renders, history entries, and saves.
 */
export function pruneTodayItems(today: TodayList, tree: TodoTree): TodayList {
  const seen = new Set<string>();
  const items = today.items.filter(id => {
    if (seen.has(id)) return false;
    const item = tree[id];
    if (!item || item.archived) return false;
    seen.add(id);
    return true;
  });

  return items.length === today.items.length ? today : { ...today, items };
}

/**
 * Validate and clean a `today` value read from a file.
 *
 * Returns undefined for anything malformed or empty rather than an error: a
 * corrupt day plan is not worth refusing to open a file over, and an absent
 * plan is a perfectly normal state.
 */
export function sanitizeTodayList(raw: unknown, tree: TodoTree): TodayList | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;

  const candidate = raw as { date?: unknown; items?: unknown };
  if (typeof candidate.date !== 'string' || !DATE_PATTERN.test(candidate.date)) return undefined;
  if (!Array.isArray(candidate.items)) return undefined;

  const items = candidate.items.filter((id): id is string => typeof id === 'string');
  const pruned = pruneTodayItems({ date: candidate.date, items }, tree);

  return pruned.items.length > 0 ? pruned : undefined;
}
